/**
 * A **unica** lista de itens de navegacao do CRM (UX-DR2).
 *
 * Sidebar, barra inferior do celular e arvore de rotas leem daqui. Escrita tres vezes, ela
 * divergiria na primeira tela nova — e o sintoma seria um item alcancavel no computador e
 * ausente no celular, que e exatamente o que a NFR-1 proibe.
 *
 * Os itens, os rotulos e a regra de perfil sao literais do prototipo aprovado
 * (`front exemplo/CRM Orvalho do Sol.dc.html`, `renderVals`): seis itens para todo mundo,
 * "Originados" no lugar de "Relatorios" para o SDR, e "Configuracao" so para quem tem escopo
 * `todas`. As cinco abas do celular e os icones delas tambem sao literais.
 *
 * **Desde a 11.2** (decisao 12 de 23/09/2026) a antiga "Administracao" (`/administracao`) e
 * **Configuracao** (`/configuracao`), com abas — e o caminho antigo deixou de existir, sem
 * redirect: quem o tinha decorado cai no fallback do SPA. Nasce tambem **Integracoes**
 * (`/integracoes`, FR-82), para os quatro perfis: a conexao do proprio numero mora la.
 *
 * **Desde a 3.11 o item do SDR tambem troca de destino**, e nao so de rotulo: "Originados" leva a
 * `/originados`, que e tela de verdade (a lista do que ele originou, FR-35). E desde a 7.2
 * `/relatorios` tambem deixou de ser placeholder: os demais perfis chegam a tela com a conversao
 * por Responsavel e a conversao de funil por Etapa (FR-54, FR-55, FR-56). Com a 7.5 aquela tela
 * ficou **completa** — os sete relatorios do FR-55, mais o Ranking (FR-58) e o Por Origem (FR-59)
 * —, e o `origemDoConteudo` dos dois itens deixou de prometer entrega nenhuma: ele passou a
 * descrever o que a area entrega hoje.
 *
 * **Rotulo com acento, identificador sem.** O que aparece na tela segue o prototipo e a
 * NFR-8 ("Relatorios" se escreve *Relatórios*); chave, caminho, nome de arquivo e
 * comentario seguem o resto do repositorio, sem acento.
 *
 * **Esconder item nao e autorizar.** Esta lista e conveniencia: a checagem de verdade e do
 * backend (`compartilhado/autorizacao.py`), e continua valendo mesmo que alguem digite a
 * URL de uma tela que o menu dele nao mostra.
 */

import type { EscopoDeCarteira, Perfil } from '@/modulos/identidade/api'

/**
 * O ponto de virada desktop/celular: **768px e celular**, 769px e computador.
 *
 * Ele mora aqui, em JavaScript, e nao numa `@media` do CSS — o `shell.css` nao tem nenhuma —
 * porque a virada nao e de estilo, e de **arvore**: o shell renderiza a sidebar **ou** a barra
 * inferior, nunca as duas escondendo uma. Duas arvores coexistindo dariam dois links
 * "Empresas" na mesma pagina, um invisivel, e todo teste de acessibilidade e de ponta a ponta
 * teria de desempatar entre eles. Quem le este numero e o `useEhCelular` do `Shell.tsx`, por
 * `matchMedia`; este e o unico lugar em que ele esta escrito.
 */
export const LARGURA_MAXIMA_DO_CELULAR = 768

/**
 * Os caminhos das telas internas, como **literais**.
 *
 * Eles sao um objeto congelado, e nao strings soltas nos itens, por dois motivos: a arvore de
 * rotas precisa do tipo literal de cada caminho para tipar `<Link to=...>` (um `string` cru
 * apagaria a checagem de destino), e `Record<CaminhoInterno, …>` passa a poder exigir que
 * exista uma rota para **cada** item do menu — item sem rota vira erro de compilacao, e nao um
 * 404 dentro do proprio shell.
 */
export const CAMINHOS = {
  hoje: '/hoje',
  funil: '/funil',
  tarefas: '/tarefas',
  whatsapp: '/whatsapp',
  empresas: '/empresas',
  pessoas: '/pessoas',
  relatorios: '/relatorios',
  originados: '/originados',
  aguardandoEntrega: '/aguardando-entrega',
  integracoes: '/integracoes',
  configuracao: '/configuracao',
} as const

/**
 * O caminho de "Gerenciar funis e etapas" (11.2, espinha de experiência): uma tela **filha** do
 * Funil, aberta por um botão dele, e nao um item de menu — por isso fica fora de `CAMINHOS`, que e
 * a lista do que o menu mostra e do que o laco de placeholders fabrica.
 */
export const CAMINHO_DE_GERENCIAR_FUNIS = '/funil/gerenciar'

/** Um dos onze caminhos internos (Pessoas entrou na 13.4). */
export type CaminhoInterno = (typeof CAMINHOS)[keyof typeof CAMINHOS]

/**
 * O titulo da tela de Originados (3.11) — escrito **uma** vez, e lido por dois lados.
 *
 * O item de navegacao do SDR o carrega em `titulo`, e a rota `/originados` o publica com
 * `useDefinirTitulo`. Os dois precisam dizer a mesma coisa: o item existe **so** para o SDR, e
 * sem a publicacao pela rota os outros tres perfis — que alcancam o caminho digitando-o, porque
 * esconder item nao e autorizar — cairiam no fallback `'CRM Orvalho do Sol'` no `<h1>`. Duas
 * copias fariam a mesma tela ter dois nomes conforme quem a abriu.
 */
export const TITULO_DOS_ORIGINADOS = 'Negócios originados'

/**
 * O titulo da tela de Relatorios (7.2) — escrito **uma** vez, e lido por dois lados.
 *
 * Ele e o irmao exato de `TITULO_DOS_ORIGINADOS`, e nasceu pela mesma razao: o item de navegacao o
 * carrega em `titulo`, e a rota `/relatorios` o publica com `useDefinirTitulo`. O item existe para
 * Vendedor, Gestor e Admin — **nao** para a SDR, que ve "Originados" no lugar dele —, e sem a
 * publicacao pela rota uma SDR que digitasse o caminho (esconder item nao e autorizar) cairia no
 * fallback `'CRM Orvalho do Sol'` no `<h1>`. Duas copias fariam a mesma tela ter dois nomes
 * conforme quem a abriu.
 */
export const TITULO_DOS_RELATORIOS = 'Relatórios'

/**
 * O titulo da tela de WhatsApp (8.1) — escrito **uma** vez, e lido por dois lados.
 *
 * Ele e o irmao exato de `TITULO_DOS_RELATORIOS`, e nasceu pela mesma razao: o item de navegacao o
 * carrega em `titulo`, e a rota `/whatsapp` o publica com `useDefinirTitulo`. O item existe para os
 * **quatro** perfis — conectar o proprio numero e permissao de todos (FR-71) —, entao aqui a
 * publicacao pela rota nao conserta um perfil sem item; ela e o que mantem o `<h1>` certo se o item
 * um dia mudar de forma. Duas copias fariam a mesma tela ter dois nomes conforme por onde se chega.
 */
export const TITULO_DO_WHATSAPP = 'WhatsApp'

/**
 * O titulo da tela "Aguardando entrega" (6.5) — escrito **uma** vez, e lido por dois lados.
 *
 * Irmao exato dos tres acima, e pela mesma razao: o item de navegacao o carrega em `titulo`, e a
 * rota o publica com `useDefinirTitulo`. Aqui o item existe para os **quatro** perfis, entao a
 * publicacao pela rota nao conserta um perfil sem item; ela e o que mantem o `<h1>` certo se o
 * item um dia mudar de forma. Duas copias fariam a mesma tela ter dois nomes.
 */
export const TITULO_DO_AGUARDANDO_ENTREGA = 'Aguardando entrega'

/**
 * A permissao que da o item de Originados a quem nao e SDR (11.5, decisao 20, FR-35 emendado): o
 * Gestor e o Admin de fabrica a tem por padrao; o Vendedor nao. A SDR tem o item no lugar de
 * Relatorios, como sempre — e continua sem Relatorios.
 */
export const PERMISSAO_DOS_ORIGINADOS = 'acessar_originados'

/** A permissao que da o item de Integracoes no menu (11.2, FR-82). */
export const PERMISSAO_DAS_INTEGRACOES = 'acessar_integracoes'

/** O titulo da tela de Integracoes (11.2, FR-82) — escrito uma vez, lido pelo item e pela rota. */
export const TITULO_DAS_INTEGRACOES = 'Integrações'

/** O titulo da tela de Configuracao (11.2) — escrito uma vez, lido pelo item e pela rota. */
export const TITULO_DA_CONFIGURACAO = 'Configuração'

/** O titulo de Gerenciar funis e etapas (11.2) — tela filha do Funil, sem item de menu. */
export const TITULO_DE_GERENCIAR_FUNIS = 'Gerenciar funis e etapas'

/** Um item do menu, ja resolvido para um perfil. */
export interface ItemDeNavegacao {
  /** Identificador estavel do item. Nao aparece na tela. */
  chave: string
  /** Rota do SPA. E o que a arvore de rotas registra. */
  caminho: CaminhoInterno
  /** Rotulo da sidebar, com acento, como o prototipo o escreve. */
  rotulo: string
  /** Rotulo da barra inferior do celular — mais curto, tambem literal do prototipo. */
  rotuloMobile: string
  /** Glifo da barra inferior. O prototipo usa caractere, e nao imagem. */
  icone: string
  /** Se ele ocupa uma das cinco abas do celular; os demais vivem na folha "Mais". */
  naBarraInferior: boolean
  /** Titulo do cabecalho de tela. */
  titulo: string
  /**
   * Subtitulo do cabecalho, quando existe.
   *
   * Fica **vazio** onde o prototipo mostra contagem, periodo ou numero de telefone: esses
   * dados nascem em servicos que o Epico 1 nao tem (Relatorios e o 6, WhatsApp o 5). O slot
   * existe e nada e inventado — a mesma regra das badges.
   *
   * Uma tela que **tenha** o dado nao o escreve aqui: ela o empresta ao cabecalho por
   * `useDefinirSubtitulo` (`shell/Shell.tsx`), que vence este campo enquanto ela estiver
   * montada. E o caso de Empresas desde a 2.6 — a contagem depende de quem esta lendo, e uma
   * constante de modulo nao tem como saber disso.
   */
  subtitulo?: string
  /** De onde o conteudo de verdade vem. A tela placeholder diz isto ao usuario. */
  origemDoConteudo: string
  /**
   * Contagem da badge (UX-DR2).
   *
   * Sempre `undefined`, e desde a 4.6 **por decisao**, e nao por falta de dado: `GET /hoje` ja
   * devolve as duas contagens de Tarefa. Acende-las aqui obrigaria o shell a ler o painel em
   * **toda** tela, so para desenhar um numero no menu, e nenhum criterio o pede. O componente nao
   * renderiza badge nenhuma quando ela nao vem.
   */
  badge?: number
}

/**
 * O escopo **fixo** dos quatro perfis de fabrica (FR-61): Admin e Gestor veem todas as
 * carteiras, Vendedor e SDR a propria. Serve de padrao quando so o perfil e conhecido.
 */
export const ESCOPO_DE_FABRICA: Record<Perfil, EscopoDeCarteira> = {
  admin: 'todas',
  gestor_comercial: 'todas',
  vendedor_closer: 'propria',
  sdr: 'propria',
}

/**
 * "Gestao" desde a 11.1 e o **escopo de carteira** `todas` da sessao — nunca o nome do perfil
 * (AD-4, AD-18). Um personalizado derivado de Vendedor com escopo `todas` e gestao; um
 * derivado de Admin com escopo `propria` nao. Conveniencia de tela: quem autoriza e o servico.
 */
export function ehDeGestao(escopo: EscopoDeCarteira): boolean {
  return escopo === 'todas'
}

/**
 * A data de hoje como o cabecalho da tela "Hoje" a mostra, em portugues do Brasil (NFR-8).
 *
 * O prototipo traz a data congelada do dia em que foi desenhado; congela-la aqui seria uma
 * tela que mente todo dia depois do primeiro. O formato e o brasileiro por extenso, que e o
 * que o prototipo desenha.
 */
export function dataDeHojePorExtenso(agora: Date = new Date()): string {
  return new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'America/Sao_Paulo',
  }).format(agora)
}

/**
 * Os itens do menu para um perfil, na ordem do prototipo.
 *
 * Tudo o que depende de perfil esta aqui e em lugar nenhum mais: o rotulo do SDR, o titulo
 * dele e a presenca de "Configuracao".
 */
export function itensPara(
  perfil: Perfil,
  escopo: EscopoDeCarteira = ESCOPO_DE_FABRICA[perfil],
  permissoes?: Readonly<Record<string, unknown>>,
): ItemDeNavegacao[] {
  const gestao = ehDeGestao(escopo)
  const sdr = perfil === 'sdr'

  const itens: ItemDeNavegacao[] = [
    {
      chave: 'hoje',
      caminho: CAMINHOS.hoje,
      rotulo: 'Hoje',
      rotuloMobile: 'Hoje',
      icone: '◉',
      naBarraInferior: true,
      titulo: 'Hoje',
      subtitulo: dataDeHojePorExtenso(),
      // **Este item nao renderiza mais `origemDoConteudo`**: `/hoje` deixou de ser placeholder na
      // 3.9 (o painel do dia tem tela propria), e quem le este campo e so a `TelaPlaceholder`. Ele
      // fica preenchido, e nao vazio, pela mesma razao dos itens de Funil, Empresas e
      // Administracao: o campo e obrigatorio na interface, e continua sendo a resposta curta a "o
      // que ainda falta nesta area". O texto diz **so** o que falta — o que ja existe (os KPIs de
      // Negocio, a lista esfriando, as tres faixas da regua e, desde a 3.10, a linha que abre a
      // ficha do Negocio) a tela mostra sozinha.
      // A fila do dia e os dois numeros de Tarefa chegaram na 4.6, e a promessa saiu junto: o
      // que sobra e o relatorio "Sem proximo passo" do Epico 7 — o KPI ja mostra o numero, e o
      // que falta e a lista por tras dele.
      origemDoConteudo:
        'Falta abrir o relatório por trás do "Sem próximo passo", que chega no Épico 7.',
    },
    {
      chave: 'funil',
      caminho: CAMINHOS.funil,
      rotulo: 'Funil',
      rotuloMobile: 'Funil',
      icone: '▤',
      naBarraInferior: true,
      titulo: 'Funil',
      subtitulo: gestao ? 'todas as carteiras' : 'seus negócios',
      // **Este item nao renderiza mais `origemDoConteudo`**: `/funil` deixou de ser placeholder
      // na 3.2 (o formulario de Negocio tem tela propria), e quem le este campo e so a
      // `TelaPlaceholder`. Ele fica preenchido, e nao vazio, pela mesma razao dos itens de
      // Empresas e de Administracao: o campo e obrigatorio na interface, e continua sendo a
      // resposta curta a "o que ainda falta nesta area". O texto diz **so** o que falta — o
      // que ja existe a tela mostra sozinha: o quadro por etapa desde a 3.8, e o card que abre a
      // ficha do Negocio — com mover, ganhar, perder, editar e excluir — desde a 3.10.
      // E o que falta no card deixou de ser "as Tarefas": elas chegaram na 4.6, com tela propria e
      // fila do dia. O que sobra e **ligar** o slot do card a elas, que e superficie da 3.8 e pede
      // uma decisao de produto (DW-153) — dizer "chega com o Epico 4" agora mandaria a pessoa
      // esperar por algo que ja esta na tela ao lado.
      origemDoConteudo: 'Falta o próximo passo aparecer no card; por ora ele mora na tela Tarefas.',
    },
    {
      chave: 'tarefas',
      caminho: CAMINHOS.tarefas,
      rotulo: 'Tarefas',
      rotuloMobile: 'Tarefas',
      icone: '✓',
      naBarraInferior: true,
      titulo: 'Tarefas',
      // **O subtitulo e vazio aqui de proposito** (4.6): quem o publica e a **tela**, por
      // `useDefinirSubtitulo`, e ele e o **filtro ativo** ("atrasadas", "hoje", "futuras",
      // "todas") — um dado que muda com o clique e que este item nao tem como saber. O
      // `'todas'` fixo que morava aqui mentia em tres das quatro abas.
      subtitulo: undefined,
      // **Este item nao renderiza mais `origemDoConteudo`**: `/tarefas` deixou de ser placeholder
      // na 4.6 (a tela tem arquivo proprio), e quem le este campo e so a `TelaPlaceholder`. Ele
      // fica preenchido, e nao vazio, pela mesma razao dos itens de Hoje, Funil e Empresas: o
      // campo e obrigatorio na interface, e continua sendo a resposta curta a "o que ainda falta
      // nesta area". O texto diz **so** o que falta — o que ja existe (os quatro filtros, a lista,
      // o modal de conclusao e a sugestao de proximo passo) a tela mostra sozinha.
      origemDoConteudo:
        'Falta criar tarefa avulsa por aqui: hoje ela nasce na ficha do negócio, ou ao aceitar a ' +
        'sugestão do próximo passo.',
    },
    {
      chave: 'whatsapp',
      caminho: CAMINHOS.whatsapp,
      rotulo: 'WhatsApp',
      rotuloMobile: 'Conversas',
      icone: '✉',
      naBarraInferior: true,
      titulo: TITULO_DO_WHATSAPP,
      // **Este item nao renderiza mais `origemDoConteudo`**: `/whatsapp` deixou de ser placeholder
      // na 8.1 (a tela tem arquivo proprio, e monta o cartao do proprio numero), e quem le este
      // campo e so a `TelaPlaceholder`. Ele fica preenchido, e nao vazio, pela mesma razao dos
      // itens de Hoje, Funil, Tarefas e Empresas: o campo e obrigatorio na interface, e continua
      // sendo a resposta curta a "o que ainda falta nesta area".
      //
      // O texto anterior prometia "o Épico 5, com a Comunicação" — dois enganos numa linha: o
      // WhatsApp e do Épico **8**, e o que falta nao e "a Comunicação" inteira, e sim a conversa,
      // que e a 8.3 (com a midia na 8.4). O que ja existe — conectar e desconectar o proprio numero
      // — a tela mostra sozinha.
      origemDoConteudo: 'Falta a conversa em si: ela chega na história 8.3, com a mídia na 8.4.',
    },
    {
      chave: 'empresas',
      caminho: CAMINHOS.empresas,
      rotulo: 'Empresas',
      rotuloMobile: 'Empresas',
      icone: '▣',
      naBarraInferior: false,
      titulo: 'Empresas',
      // **Este item nao renderiza mais `origemDoConteudo`**: `/empresas` deixou de ser
      // placeholder na 2.6 (a listagem tem tela propria), e quem le este campo e so a
      // `TelaPlaceholder`. Ele fica preenchido, e nao vazio, pela mesma razao do item de
      // Administracao: o campo e obrigatorio na interface, e continua sendo a resposta curta
      // a "o que ainda falta nesta area". O texto diz **so** o que falta — o que ja existe a
      // tela mostra sozinha.
      origemDoConteudo:
        'Falta a coluna de dias sem contato, cujo dado nasce com os Negócios, no Épico 3.',
    },
    {
      chave: 'pessoas',
      caminho: CAMINHOS.pessoas,
      rotulo: 'Pessoas',
      rotuloMobile: 'Pessoas',
      icone: '☺',
      // **Na folha "Mais", e não na barra inferior** (13.4, D-6): as abas do celular são literais
      // do protótipo, e Pessoas não veio dele — nasceu do chamado de 21/10/2026 (P13), "no estilo
      // do protótipo". Ele fica logo depois de Empresas, que é de onde a pessoa vem procurá-lo.
      naBarraInferior: false,
      titulo: 'Pessoas',
      // A tela tem arquivo próprio (`rotas/pessoas.tsx`), e quem lê este campo é só a
      // `TelaPlaceholder`: ele é a resposta curta a "o que há nesta área".
      origemDoConteudo:
        'Todos os contatos que você enxerga, com a Empresa ao lado; os do WhatsApp que ainda não ' +
        'têm Empresa aparecem só para quem os recebeu, com "Vincular a uma Empresa".',
    },
    /*
     * O sexto item **troca de destino** por perfil, e não só de rótulo — e a mudança é da 3.11.
     *
     * Até ela, "Originados" era o rótulo que a SDR via para um item que apontava para
     * `/relatorios`, o placeholder do Épico 7: o menu prometia uma tela que não existia. Agora ela
     * tem caminho e tela próprios (`/originados`, a lista do que ela originou, FR-35), e os demais
     * perfis vão para `/relatorios` — que desde a 7.2 também é tela de verdade, e não mais a
     * promessa do Épico 7.
     *
     * **Dois itens, e não um com `caminho` ternário**, porque a chave também muda: é ela que a
     * sidebar usa como `key` e que `itemDoCaminho` casa com a rota aberta — um item chamado
     * `relatorios` morando em `/originados` faria a leitura de qualquer um dos dois lugares mentir.
     *
     * A posição, o ícone e o rótulo do celular são os **mesmos**: a barra inferior é literal do
     * protótipo ("Números" nos quatro perfis), e a distinção do SDR aparece no rótulo da sidebar e
     * no título da tela. Mexer neles seria mudar o protótipo por causa de um destino.
     */
    sdr
      ? {
          chave: 'originados',
          caminho: CAMINHOS.originados,
          rotulo: 'Originados',
          rotuloMobile: 'Números',
          icone: '▦',
          naBarraInferior: true,
          titulo: TITULO_DOS_ORIGINADOS,
          // **Este item não renderiza mais `origemDoConteudo`**: `/originados` deixou de ser
          // placeholder na 3.11 (a lista tem tela própria), e quem lê este campo é só a
          // `TelaPlaceholder`. Ele fica preenchido, e não vazio, pela mesma razão dos itens de
          // Hoje, Funil, Empresas e Administração: o campo é obrigatório na interface, e continua
          // sendo a resposta curta a "o que ainda falta nesta área". O texto diz **só** o que
          // falta — o que já existe (a lista com desfecho e motivo) a tela mostra sozinha.
          //
          // A 7.4 entregou o relatório "Originados pela SDR" — ele está na tela `/relatorios`, sob
          // o mesmo seletor de período dos outros seis, e é o **relatório**, e não esta lista
          // pessoal. A 7.5 entregou o que faltava do épico: o Ranking (FR-58) e o agrupamento por
          // origem (FR-59), os dois também em `/relatorios`.
          //
          // **A frase deixou de citar o que falta porque não falta nada nesta área**, e é por isso
          // que ela agora descreve o que existe: o campo é obrigatório na interface e continua
          // sendo a resposta curta a "de onde vem o conteúdo desta área". Deixá-lo prometendo uma
          // entrega já feita seria a forma mais barata de esta resposta envelhecer — e a próxima
          // pessoa a ler o menu procuraria um Ranking que já está na tela ao lado.
          origemDoConteudo:
            'Esta é a lista pessoal de quem origina; os relatórios da área, inclusive o Ranking, ' +
            'estão em Relatórios.',
        }
      : {
          chave: 'relatorios',
          caminho: CAMINHOS.relatorios,
          rotulo: 'Relatórios',
          rotuloMobile: 'Números',
          icone: '▦',
          naBarraInferior: true,
          titulo: TITULO_DOS_RELATORIOS,
          // **Este item não renderiza mais `origemDoConteudo`**: `/relatorios` deixou de ser
          // placeholder na 7.2 (a conversão por Responsável e a conversão de funil por Etapa têm
          // tela própria), e quem lê este campo é só a `TelaPlaceholder`. Ele fica preenchido, e
          // não vazio, pela mesma razão dos itens de Hoje, Funil, Empresas, Originados e
          // Administração: o campo é obrigatório na interface, e continua sendo a resposta curta a
          // "o que ainda falta nesta área". O texto diz **só** o que falta — o que já existe a tela
          // mostra sozinha —, e o épico citado é o **7**, que é de onde os sete relatórios vêm; o
          // texto anterior apontava para o 6, que é o das RATs.
          //
          // A 7.3 entregou os dois relatórios de detecção, a 7.4 os três últimos do FR-55 e a 7.5
          // as duas seções que fechavam o épico: o Ranking (FR-58) e o agrupamento por origem
          // (FR-59). O texto encolheu de cinco para três, de três para um, e agora para **nenhum**.
          //
          // **Não falta mais nada nesta área**, e por isso a frase mudou de natureza: em vez de
          // contar o que vem, ela conta o que está lá. O campo continua obrigatório na interface e
          // continua sendo a resposta curta a "de onde vem o conteúdo desta área"; um texto que
          // continuasse prometendo o Ranking apontaria para uma seção que já existe duas rolagens
          // abaixo.
          //
          // **O texto não diz "sob um seletor só", e a precisão é da 7.5**: o Ranking tem período
          // próprio, com o seletor dele dentro da própria seção (FR-58). Uma frase que juntasse as
          // nove seções sob um seletor seria falsa a partir da segunda rolagem — e é justamente a
          // separação que faz o quadro abrir na semana enquanto o resto abre no mês.
          origemDoConteudo:
            'Os sete relatórios do FR-55, mais o Ranking de vendedores e o resultado por origem. ' +
            'O Ranking tem período próprio; as demais seções compartilham um seletor.',
        },
  ]

  // **O item da 6.5 existe para os quatro perfis, e a SDR entrou na revisão de 16/09/2026.** Ele
  // nasceu escondido dela, com a justificativa de que "a SDR não é Responsável de Negócio nenhum"
  // — e essa premissa é falsa: `dominio/rls.py` diz o oposto ("quem entrega é a SDR, que é a
  // Responsável na Prospecção"), e a expressão de visibilidade entrega todo Negócio de funil de
  // Prospecção a **qualquer** usuário comercial (FR-31). Esconder o item dela escondia trabalho
  // real. Ele está fora do array literal acima porque os seis de lá são do protótipo aprovado e
  // este não é: a tela nasceu de decisão posterior a ele.
  // **Originados tambem para a gestao (11.5, decisao 20)**: quem nao e SDR e tem
  // `acessar_originados` ganha o item na folha "Mais" (a barra inferior continua literal do
  // prototipo). Sem `permissoes` (chamada sem sessao completa), vale o padrao do FR-61-A: a gestao.
  const temOriginados =
    permissoes === undefined ? gestao || sdr : PERMISSAO_DOS_ORIGINADOS in permissoes
  // **A SDR tambem precisa da permissao (revisao da 11.5)**: o item do lugar de "Numeros" e o dela
  // por padrao, mas perfil personalizado derivado de SDR pode ter `acessar_originados` negada — e
  // a rota responde `403`. Sem a permissao o item sai (e a SDR continua sem Relatorios).
  if (sdr && !temOriginados) {
    const posicao = itens.findIndex((item) => item.chave === 'originados')
    if (posicao >= 0) {
      itens.splice(posicao, 1)
    }
  }
  if (!sdr && temOriginados) {
    itens.push({
      chave: 'originados',
      caminho: CAMINHOS.originados,
      rotulo: 'Originados',
      rotuloMobile: 'Originados',
      icone: '↗',
      naBarraInferior: false,
      titulo: TITULO_DOS_ORIGINADOS,
      origemDoConteudo:
        'Os negócios que as SDRs originaram, depois da entrega para Vendas: com quem estão, ' +
        'ganhos ou perdidos com o motivo. Sem valor.',
    })
  }

  itens.push({
    chave: 'aguardando-entrega',
    caminho: CAMINHOS.aguardandoEntrega,
    rotulo: 'Aguardando entrega',
    rotuloMobile: 'Entregas',
    icone: '⧗',
    // **Na folha, e não na barra inferior**: os quatro lugares da barra são do protótipo, e
    // empurrar um deles mudaria um gesto já decorado por uma tela que se visita uma vez ao dia.
    naBarraInferior: false,
    titulo: TITULO_DO_AGUARDANDO_ENTREGA,
    subtitulo: gestao ? 'todas as carteiras' : 'seus negócios',
    // A área não tem o que faltar: a lista, o campo de data e o estado vazio são a história
    // inteira. O que este texto descreve é de onde vem o conteúdo — a pergunta que o campo é
    // obrigatório para responder.
    origemDoConteudo:
      'Os negócios ganhos que ainda não têm data de entrega registrada. Preencher a data aqui ' +
      'é o que acende a indicação de garantia na RAT.',
  })

  // **Integrações (11.2, FR-82) é de quem tem `acessar_integracoes`**: a permissão nasce concedida
  // nos quatro perfis de fábrica, e é ali que cada um conecta o próprio número (FR-71) — mas um
  // perfil personalizado pode tê-la negada, e o item some junto (revisão da 11.2). A claim só
  // carrega as permissões não negadas, e por isso o teste é `in`. Sem `permissoes` (chamada sem
  // sessão, só com o perfil de fábrica) vale o padrão de fábrica: concedida. O que o perfil recorta
  // dentro da tela — as credenciais só para quem pode editá-las — quem recorta é o serviço.
  if (permissoes === undefined || PERMISSAO_DAS_INTEGRACOES in permissoes) itens.push({
    chave: 'integracoes',
    caminho: CAMINHOS.integracoes,
    rotulo: 'Integrações',
    rotuloMobile: 'Integrações',
    icone: '⇄',
    naBarraInferior: false,
    titulo: TITULO_DAS_INTEGRACOES,
    // O subtítulo do protótipo v2: a gestão vê as três integrações; quem não é gestão só o seu.
    subtitulo: gestao ? 'WhatsApp, Carga Máquina e e-mail' : 'seu WhatsApp',
    origemDoConteudo:
      'WhatsApp (o seu número e o provedor), Carga Máquina (credenciais e recebimentos do ' +
      'conector) e E-mail (IMAP e destinatários do aviso de RAT).',
  })

  if (gestao) {
    itens.push({
      chave: 'configuracao',
      caminho: CAMINHOS.configuracao,
      rotulo: 'Configuração',
      rotuloMobile: 'Configuração',
      icone: '⚙',
      naBarraInferior: false,
      titulo: TITULO_DA_CONFIGURACAO,
      // O subtítulo do protótipo v2, com a aba de Metas da 11.9.
      subtitulo: 'negócios, clientes, tarefas, usuários, permissões e metas',
      // A tela tem arquivo próprio (`rotas/configuracao.tsx`), e quem lê este campo é só a
      // `TelaPlaceholder`. Ele continua sendo a resposta curta a "o que há nesta área": as seis
      // abas — a de Metas chegou na 11.9.
      origemDoConteudo: 'Negócios, Clientes, Tarefas, Usuários, Permissões e Metas.',
    })
  }

  return itens
}

/**
 * Todos os caminhos que a arvore de rotas precisa registrar, na ordem do menu.
 *
 * Sao os de **todos** os perfis, e nao os de um: a rota existe para todo mundo — quem nao tem
 * o item no menu ainda pode digitar a URL —, e quem recusa e o backend. Sem isto, o SDR
 * digitando `/configuracao` cairia num 404 do SPA em vez de na recusa de verdade, e
 * "esconder item nao e autorizar" viraria letra morta.
 */
export const CAMINHOS_INTERNOS = Object.values(CAMINHOS)

/** O item cujo caminho e o da rota aberta, se houver. */
export function itemDoCaminho(
  itens: readonly ItemDeNavegacao[],
  caminho: string,
): ItemDeNavegacao | undefined {
  return itens.find((item) => item.caminho === caminho)
}

/** Os itens que **nao** cabem nas cinco abas: eles vivem na folha "Mais" (NFR-1). */
export function itensDaFolha(itens: readonly ItemDeNavegacao[]): ItemDeNavegacao[] {
  return itens.filter((item) => !item.naBarraInferior)
}

/** As abas da barra inferior, na ordem do prototipo. */
export function itensDaBarraInferior(itens: readonly ItemDeNavegacao[]): ItemDeNavegacao[] {
  return itens.filter((item) => item.naBarraInferior)
}

/**
 * O rotulo e o glifo da sexta aba.
 *
 * O prototipo tem cinco abas e nao tem Empresas nem Configuracao no celular; a NFR-1 nao
 * deixa nenhum item ficar so no computador. "Mais" e a saida que nao mexe em aba nenhuma do
 * prototipo: as cinco continuam identicas e a sexta abre a folha com o resto, o bloco do
 * usuario e "Sair".
 */
export const ABA_MAIS = { rotulo: 'Mais', icone: '≡' } as const

